import ApplicationController from '@app/javascript/controllers/application_controller'
import Highcharts from 'highcharts'
import { useDispatch } from 'stimulus-use'
import Rails from '@rails/ujs'
import { flashError } from '@app/javascript/classes/flashMessages'

export default class extends ApplicationController {
  static values = { path: String, searchParams: String, width: Number }
  static targets = ['loadingIndicator', 'form']

  connect() {
    useDispatch(this)
    this.triggerCreateCharts()
  }

  triggerCreateCharts() {
    this.loadingIndicatorTarget.removeAttribute('hidden')
    for (const selector of ['#top_issues_chart', '#top_labels_chart', '#top_actions_chart', '#top_stakeholders_chart']) {
      this.updateChartVisibility(document.querySelector(selector), false)
    }
    Rails.ajax({
      type: 'GET',
      url: this.pathValue || '/actions/chart_data',
      dataType: 'json',
      data: this.searchParamsValue || $('#actions-filter-form').serialize(),
      success: (data) => {
        this.loadingIndicatorTarget.setAttribute('hidden', '')
        this.createCharts(data)
      },
      error: () => flashError('Could not load visualizations. Please try again later.')
    })
  }

  createCharts(data) {
    this.visibleCharts = []
    this.createBarCharts(data)
    if (this.visibleCharts.length > 0) {
      this.element.classList.remove('hidden')
    } else {
      this.element.classList.add('hidden')
    }
  }

  createBarCharts(data) {
    const showTopIssues = data.issues.length > 0
    this.createBarChart('#top_issues_chart', 'Top Issues by Number of Actions', data.issues, 'issueSelected', showTopIssues)
    const showTopLabels = data.labels.length > 0
    this.createBarChart('#top_labels_chart', 'Top Labels by Number of Actions', data.labels, 'labelSelected', showTopLabels)
    const showTopActionTypes = data.action_types.length > 0
    this.createBarChart('#top_actions_chart', 'Top Action Types by Number of Actions', data.action_types, 'actionTypeSelected', showTopActionTypes)
    const showTopStakeholders = data.stakeholders.length > 0
    this.createBarChart('#top_stakeholders_chart', 'Top Stakeholders by Number of Actions', data.stakeholders, 'stakeholderSelected', showTopStakeholders)
  }

  createBarChart(selector, title, inputData, clickMethod, visible = true) {
    const self = this
    const container = document.querySelector(selector)
    if (!this.updateChartVisibility(container, visible)) {
      return
    }

    const data = inputData.map(entry => entry.count)
    const seriesMax = Math.floor(Math.max(...data) * 1.05)

    const highchartsOptions = {
      spacingBottom: 30,
      spacingRight: 30,
      spacingTop: 30,
      type: 'bar',
      height: 300,
      marginLeft: 200,
      events: {
        render: function() {
          const chart = this
          chart.series[1].points.forEach(singleData => {
            if (!singleData.customLabel) {
              singleData.customLabel = chart.renderer.text(
                singleData.y,
                chart.plotLeft + chart.plotWidth,
                chart.plotHeight + chart.plotTop + 5 - singleData.plotX
              )
                .add()
            } else {
              singleData.customLabel.attr({
                x: chart.plotLeft + chart.plotWidth,
                y: chart.plotHeight + chart.plotTop + 5 - singleData.plotX
              })
            }
          })
        }
      }
    }
    if (this.widthValue) {
      highchartsOptions.width = this.widthValue
    }

    Highcharts.chart(container, {
      chart: highchartsOptions,
      title: {
        text: title
      },
      xAxis: {
        categories: inputData.map(entry => entry.text),
        labels: {
          style: {
            fontSize: '10px',
            width: 180,
            whiteSpace: 'break-spaces'
          },
          allowOverlap: false
        }
      },
      yAxis: {
        visible: false,
        max: seriesMax
      },
      tooltip: {
        formatter: function() {
          return this.x + ': ' + this.y + ' actions'
        }
      },
      responsive: {},
      plotOptions: {
        series: {
          cursor: 'pointer',
          stacking: 'normal',
          maxPointWidth: 20,
          point: {
            events: {
              click: function() {
                const entry = inputData.find(item => item.text === this.category)
                return self.dispatch(clickMethod, { name: this.category, value: entry.value })
              }
            }
          }
        }
      },
      legend: {
        enabled: false
      },
      series: [{
        data: data.map(singleData => seriesMax - singleData),
        color: '#ebebeb',
        pointPadding: 0.01,
        enableMouseTracking: false
      }, {
        data: data,
        color: '#764b88',
        pointPadding: 0.01
      }],
      credits: {
        text: 'Credit: FiscalNote',
        href: 'https://fiscalnote.com/'
      },
      exporting: {
        enabled: false
      }
    })
  }

  exportingOptions() {
    return {
      buttons: {
        contextButton: {
          // Returns menu items without the viewFullscreen button since that doesn't work in an iframe.
          menuItems: ['printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG']
        }
      }
    }
  }

  updateChartVisibility(container, isVisible) {
    container.style.display = isVisible ? 'block' : 'none'
    if (isVisible) {
      this.visibleCharts.push(container)
    }
    return isVisible
  }
}
